import { useMemo, useState, useEffect } from 'react';
import type { CycleRing } from '@/lib/cycleTypes';
import { ArrowDown, ArrowUp, Eye, EyeOff, Plus, X, Pencil, Settings2, Info, Sparkles } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PRESET_CYCLES } from '@/lib/cycleConfig';
import type { RingTheme } from '@/lib/ringThemeStore';
import { loadCycleGroups, subscribeCycleGroups, loadSelectedGroupId, subscribeSelectedGroup } from '@/lib/cycleGroupStore';
import type { CycleGroupData } from '@/lib/cycleGroupStore';

const RING_ORDER: Record<string, number> = {
  atomicSecond: 0,
  atomicMinute: 1,
  atomicHour: 2,
  solarSecond: 3,
  solarMinute: 4,
  solarHour: 5,
  sun: 6,
  days: 7,
  earth: 8,
  moons: 9,
  lunar: 10,
  plasmas: 11,
  colors: 12,
  seals: 13,
  families: 14,
  quarters: 15,
  mercury: 16,
  venus: 17,
  mars: 18,
  jupiter: 19,
  saturn: 20,
  uranus: 21,
  neptune: 22,
  weeks: 23,
  tzolkin: 24,
  'gregorian-leap': 25,
  // Space Weather Cycles (outermost rings)
  'geomagnetic-activity': 50,
  'solar-wind-pressure': 51,
  'electron-flux': 52,
  'alert-severity': 53,
  'imf-bz': 54,
};

// Rings that cannot be removed from dashboard
const CORE_RINGS = new Set(['days', 'moons', 'geomagnetic-activity', 'solar-wind-pressure', 'electron-flux', 'alert-severity', 'imf-bz']);

export interface CycleLegendProps {
  viewId?: string | null;
  rings: CycleRing[];
  onToggleRing?: (ringId: string) => void;
  onAddRing?: (presetId: string) => void;
  onRemoveRing?: (ringId: string) => void;
  onEditRing?: (ring: CycleRing) => void;
  onOpenWizard?: () => void;
  onOpenThemeWizard?: () => void;
  onReorderRings?: (orderedIds: string[]) => void;
  onSetSkinMode?: (ringId: string, mode: CycleRing['skinMode'] | null) => void;
  onSetVisibleCount?: (count: number) => void;
  hiddenRings?: string[];
  ringThemes?: RingTheme[];
  visibleCount?: number;
  className?: string;
}

const CycleLegend = ({ 
  viewId,
  rings, 
  onToggleRing, 
  onAddRing, 
  onRemoveRing,
  onEditRing,
  onOpenWizard,
  onOpenThemeWizard,
  hiddenRings = [],
  onReorderRings,
  onSetSkinMode,
  onSetVisibleCount,
  ringThemes = [],
  visibleCount,
}: CycleLegendProps) => {
  const [detailsRing, setDetailsRing] = useState<CycleRing | null>(null);
  const [cycleGroups, setCycleGroups] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(() => loadSelectedGroupId(viewId));

  useEffect(() => {
    let active = true;
    const handleGroups = (groups: CycleGroupData[]) => {
      if (!active) return;
      setCycleGroups(groups.map(g => ({ id: g.id, name: (g?.name ?? 'Unnamed Group') })));
    };
    const unsub = subscribeCycleGroups(viewId, handleGroups);
    if (!unsub) {
      loadCycleGroups(viewId).then(handleGroups).catch(() => undefined);
    }
    const unsubSel = subscribeSelectedGroup(viewId, (id) => setSelectedGroupId(id));
    return () => { active = false; unsub?.(); unsubSel?.(); };
  }, [viewId]);
  
  // Get available presets that aren't already added
  const availablePresets = Object.entries(PRESET_CYCLES)
    .filter(([id]) => !rings.some(r => r.id === id))
    .sort(([idA, presetA], [idB, presetB]) => {
      const orderA = RING_ORDER[idA] ?? 999;
      const orderB = RING_ORDER[idB] ?? 999;
      if (orderA !== orderB) return orderA - orderB;
      return (presetA?.name ?? '').localeCompare(presetB?.name ?? '');
    });

  // Check if a ring can be removed (not core rings like days/moons and space weather)
  const canRemove = (ringId: string) => {
    return !CORE_RINGS.has(ringId);
  };

  const orderedRings = useMemo(() => {
    return [...rings].sort((a, b) => {
      const orderA = a.displayOrder ?? RING_ORDER[a.id] ?? 999;
      const orderB = b.displayOrder ?? RING_ORDER[b.id] ?? 999;
      return orderA - orderB;
    });
  }, [rings]);

  const handleMoveRing = (ringId: string, direction: -1 | 1) => {
    if (!onReorderRings) return;
    const orderedIds = orderedRings.map((ring) => ring.id);
    const currentIndex = orderedIds.indexOf(ringId);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= orderedIds.length) return;
    [orderedIds[currentIndex], orderedIds[nextIndex]] = [orderedIds[nextIndex], orderedIds[currentIndex]];
    onReorderRings(orderedIds);
  };

  const skinOptions = [
    { mode: 'primary' as const, label: 'Primary', color: 'bg-emerald-400' },
    { mode: 'secondary' as const, label: 'Secondary', color: 'bg-sky-400' },
    { mode: 'tertiary' as const, label: 'Tertiary', color: 'bg-violet-400' },
  ];

  const detailsParent = useMemo(() => {
    if (!detailsRing?.parentId) return null;
    return (
      rings.find((ring) => ring.id === detailsRing.parentId) ??
      Object.values(PRESET_CYCLES).find((preset) => preset.id === detailsRing.parentId) ??
      null
    );
  }, [detailsRing, rings]);

  const detailsStageLabel = detailsRing
    ? detailsRing.labels[detailsRing.currentStage] || String(detailsRing.currentStage + 1)
    : '';

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-border/30">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cycles</h3>
        {selectedGroupId && (
          <div className="ml-2 text-sm text-muted-foreground truncate max-w-[220px]">
            {cycleGroups.find(g => g.id === selectedGroupId)?.name ?? 'Group'}
          </div>
        )}
        <div className="flex items-center gap-1">
          {onOpenThemeWizard && (
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onOpenThemeWizard} title="Open Ring Themes">
              <Sparkles className="w-3.5 h-3.5" />
            </Button>
          )}
          {onOpenWizard && (
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onOpenWizard} title="Open Cycle Wizard">
              <Settings2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
      
      <ScrollArea className="flex-1">
        <div className="space-y-2 pr-2">
          {/* Active rings */}
          {orderedRings.map((ring, idx) => {
            const isExplicitlyHidden = hiddenRings.includes(ring.id);
            const isCountHidden = typeof visibleCount === 'number' && idx >= visibleCount;
            const isHidden = isExplicitlyHidden || isCountHidden;
            const currentLabel = ring.labels[ring.currentStage] || String(ring.currentStage + 1);
            const currentColor = ring.colors[ring.currentStage];
            const removable = canRemove(ring.id);
            
            return (
              <div 
                key={ring.id}
                className={`rounded-lg p-3 border transition-all group/ring active:scale-[0.98] ${
                  isHidden 
                    ? 'bg-card/20 border-border/20 opacity-50' 
                    : 'bg-card/60 border-border/40 hover:border-border/60 hover:bg-card/80'
                }`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <div 
                    className={`w-3 h-3 rounded-full flex-shrink-0 ${isHidden ? 'opacity-40' : ''}`}
                    style={{ backgroundColor: `hsl(${currentColor})` }}
                  />
                  <span className={`text-xs font-medium truncate flex-1 ${isHidden ? 'text-muted-foreground' : ''}`}>
                    {ring.name ?? 'Unnamed'}
                  </span>
                  {CORE_RINGS.has(ring.id) && !['days', 'moons'].includes(ring.id) && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-950/40 border border-amber-700/50 text-amber-300">
                      Live
                    </span>
                  )}
                  {ring.themeId && (
                    <span className="text-[9px] uppercase tracking-wider text-primary/80">
                      {ringThemes.find((theme) => theme.id === ring.themeId)?.name ?? 'Theme'}
                    </span>
                  )}
                  {ring.skinMode && (
                    <span className="text-[9px] uppercase tracking-wider text-primary/80">
                      {ring.skinMode}
                    </span>
                  )}
                  <div className="flex items-center gap-1">
                    {onReorderRings && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleMoveRing(ring.id, -1)}
                          className="p-1 hover:bg-background/50 rounded transition-colors"
                          title="Move ring inward"
                        >
                          <ArrowUp className="w-3.5 h-3.5 text-muted-foreground hover:text-primary" />
                        </button>
                        <button
                          onClick={() => handleMoveRing(ring.id, 1)}
                          className="p-1 hover:bg-background/50 rounded transition-colors"
                          title="Move ring outward"
                        >
                          <ArrowDown className="w-3.5 h-3.5 text-muted-foreground hover:text-primary" />
                        </button>
                      </div>
                    )}
                    {onToggleRing && (
                      <button
                        onClick={() => {
                          if (isCountHidden && !isExplicitlyHidden && onSetVisibleCount) {
                            // Row is dimmed only by the "show N rings" density gate — raise the
                            // count to reveal it. Toggling hiddenRings can't clear that gate and
                            // would double-hide the row on the first click.
                            onSetVisibleCount(idx + 1);
                          } else {
                            onToggleRing(ring.id);
                          }
                        }}
                        className="p-1 hover:bg-background/50 rounded transition-colors"
                        title={isHidden ? 'Show ring' : 'Hide ring'}
                      >
                        {isHidden ? (
                          <EyeOff className="w-3.5 h-3.5 text-muted-foreground" />
                        ) : (
                          <Eye className="w-3.5 h-3.5 text-primary" />
                        )}
                      </button>
                    )}
                    {onEditRing && (
                      <button
                        onClick={() => {
                          try {
                            onEditRing?.(ring);
                          } catch (err) {
                            // Guard against unexpected errors in parent handlers
                            console.error('onEditRing error', err);
                          }
                        }}
                        className="p-1 hover:bg-background/50 rounded transition-colors"
                        title="Edit cycle"
                      >
                        <Pencil className="w-3.5 h-3.5 text-muted-foreground hover:text-primary" />
                      </button>
                    )}
                    <button
                      onClick={() => setDetailsRing(ring)}
                      className="p-1 hover:bg-background/50 rounded transition-colors"
                      title="View details"
                    >
                      <Info className="w-3.5 h-3.5 text-muted-foreground hover:text-primary" />
                    </button>
                    {removable && onRemoveRing && (
                      <button
                        onClick={() => onRemoveRing(ring.id)}
                        className="p-1 hover:bg-destructive/20 rounded transition-colors"
                        title="Remove cycle"
                      >
                        <X className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
                      </button>
                    )}
                  </div>
                </div>
                
                {!isHidden && (
                  <>
                    {/* Current stage indicator */}
                    <div className="flex items-center gap-1 text-[10px]">
                      <span 
                        className="font-semibold px-1 py-0.5 rounded"
                        style={{ 
                          backgroundColor: `hsl(${currentColor} / 0.2)`,
                          color: `hsl(${currentColor})`
                        }}
                      >
                        {currentLabel}
                      </span>
                      <span className="text-muted-foreground">
                        ({ring.currentStage + 1}/{ring.stages})
                      </span>
                    </div>
                    
                    {/* Mini stage preview */}
                    <div className="flex gap-0.5 mt-1 overflow-hidden">
                      {Array.from({ length: Math.min(ring.stages, 10) }).map((_, i) => {
                        const stageIndex = ring.stages <= 10 
                          ? i 
                          : Math.floor((ring.currentStage - 2 + i + ring.stages) % ring.stages);
                        const isCurrentStage = stageIndex === ring.currentStage;
                        const color = ring.colors[stageIndex];
                        
                        return (
                          <div
                            key={i}
                            className={`h-1 flex-1 rounded-sm transition-all ${
                              isCurrentStage ? 'ring-1 ring-white scale-y-150' : ''
                            }`}
                            style={{ backgroundColor: `hsl(${color})` }}
                          />
                        );
                      })}
                    </div>
                    {onSetSkinMode && (
                      <div className="mt-2 flex items-center gap-2 text-[10px] text-muted-foreground">
                        <span className="uppercase tracking-wider">Skin</span>
                        <div className="flex items-center gap-1">
                          {skinOptions.map((option) => (
                            <button
                              key={option.mode}
                              onClick={() =>
                                onSetSkinMode(
                                  ring.id,
                                  ring.skinMode === option.mode ? null : option.mode
                                )
                              }
                              className={`h-4 w-4 rounded-full border ${
                                ring.skinMode === option.mode
                                  ? `border-white ${option.color}`
                                  : 'border-border/60 bg-background/40'
                              }`}
                              title={`${option.label} dashboard ring`}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
          
          {/* Available presets to add */}
          {onAddRing && availablePresets.length > 0 && (
            <div className="pt-2 border-t border-border/30">
              <p className="text-[10px] text-muted-foreground mb-1.5 uppercase tracking-wider">Add Cycle</p>
              <div className="space-y-1">
                {availablePresets.map(([id, preset]) => (
                  <button
                    key={id}
                    onClick={() => onAddRing(id)}
                    className="w-full flex items-center gap-2 p-1.5 rounded text-left hover:bg-card/50 transition-colors group"
                  >
                    <Plus className="w-3 h-3 text-muted-foreground group-hover:text-primary" />
                    <span className="text-xs text-muted-foreground group-hover:text-foreground truncate">
                      {preset.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Indicators */}
          <div className="pt-2 border-t border-border/30">
            <p className="text-[10px] text-muted-foreground mb-1 uppercase tracking-wider">Indicators</p>
            <div className="space-y-0.5">
              <div className="flex items-center gap-2 text-[10px]">
                <div className="w-2.5 h-2.5 rounded-full bg-amber-500/70 flex items-center justify-center">
                  <span className="text-[5px]">✧</span>
                </div>
                <span className="text-muted-foreground">The Drift</span>
              </div>
              <div className="flex items-center gap-2 text-[10px]">
                <div className="w-2.5 h-2.5 rounded-full bg-purple-400/70" />
                <span className="text-muted-foreground">Lunar Phase</span>
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>

      <Dialog open={!!detailsRing} onOpenChange={(open) => !open && setDetailsRing(null)}>
        <DialogContent className="sm:max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-display">
              {detailsRing?.name ?? 'Cycle details'}
            </DialogTitle>
          </DialogHeader>
          {detailsRing && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg border border-border/40 p-2">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Stages</p>
                  <p className="font-medium">{detailsRing.stages}</p>
                </div>
                <div className="rounded-lg border border-border/40 p-2">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Waveform</p>
                  <p className="font-medium capitalize">{detailsRing.waveform ?? 'default'}</p>
                </div>
                <div className="rounded-lg border border-border/40 p-2">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Current Stage</p>
                  <p className="font-medium">{detailsStageLabel}</p>
                </div>
                <div className="rounded-lg border border-border/40 p-2">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Stage Index</p>
                  <p className="font-medium">
                    {detailsRing.currentStage + 1}/{detailsRing.stages}
                  </p>
                </div>
              </div>
              {detailsParent && (
                <div className="rounded-lg border border-border/40 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Orbit / Parent Cycle</p>
                  <p className="font-medium">{detailsParent.name}</p>
                  {detailsRing.stagesPerParent !== undefined && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {detailsRing.stagesPerParent} stages per parent stage
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CycleLegend;
